// src/components/practice/tasks/TextReconstructionEndings.jsx
import React from 'react';
import { marked } from 'marked';
import { FlagButton } from '../PartDisplay';
import { X } from 'lucide-react';

const TextReconstructionEndings = React.memo(({
  partData,
  questions,
  examples,
  partAnswers,
  handleSingleAnswer,
  flaggedQuestions,
  toggleFlagQuestion,
  isMobileAnswerSheetOpen,
  onCloseMobileAnswerSheet
}) => {
    const allProps = { flaggedQuestions, toggleFlagQuestion };
    const { content } = partData;
    const mainText = content?.mainText || '';

    // This regex finds [a] OR [1]
    const placeholderRegex = /\[([a-zA-Z0-9]+)\]/g;

    // Create a map for quick example lookup (still needed for answer sheet)
    const exampleMap = examples.reduce((acc, ex) => {
      acc[ex.blankNumber] = ex;
      return acc;
    }, {});

    // --- LEFT COLUMN CONTENT (Reading) ---
    const leftContent = (
      <div className="space-y-6">

        {/* --- Examples block REMOVED from here --- */}

        {/* Main Text with Gaps */}
        <div className="border-2 border-gray-900 bg-white p-6 rounded-none">
            <div className="prose prose-sm max-w-none text-gray-900 leading-relaxed font-serif whitespace-pre-wrap">
              {/* Split by newline to render as paragraphs */}
              {mainText.split('\n').map((line, lineIndex) => (
                <p key={lineIndex}>
                  {line.split(placeholderRegex).map((part, index) => {
                    if (index % 2 === 0) {
                      // This is a regular text part
                      return <span key={`text-${index}`} dangerouslySetInnerHTML={{ __html: marked.parseInline(part, { breaks: true }) }} />;
                    }

                    // --- THIS IS THE CHANGE ---
                    // This is a placeholder, e.g., "a" or "1"
                    const blankNumber = part; // The captured group

                    // Render ALL gaps with the same style
                    return (
                        <span key={`blank-${index}`} className="inline-block text-center font-bold mx-1 px-3 py-1 bg-gray-100 border-2 border-gray-900 rounded-none">
                            [{blankNumber}]
                        </span>
                    );
                    // --- END CHANGE ---
                  })}
                </p>
              ))}
            </div>
        </div>
      </div>
    );

    // --- RIGHT COLUMN CONTENT (Answering) ---
    // (This part is already correct and will render the 3 example boxes)
    const rightContent = (
      <div className="space-y-1">
        {/* Answer Sheet Header (Unchanged) */}
        <div className="border-2 border-gray-900 bg-white p-3 rounded-none mb-4">
          <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
            Antwortbogen
          </h3>
          <p className="text-xs text-gray-600 mt-1">
            Tragen Sie Ihre Antworten ein.
          </p>
        </div>

        {/* --- Examples List --- */}
        {examples.map(ex => {
          const questionId = ex.questionId;
          const correctAnswer = Array.isArray(ex.correctAnswer) ? ex.correctAnswer[0] : ex.correctAnswer;

          return (
            <div
              key={questionId}
              className="border border-gray-900 bg-yellow-50 p-4 rounded-none opacity-90"
            >
              {/* Example Label */}
              <div className="flex items-start justify-between mb-2">
                <label
                  htmlFor={`input-${questionId}`}
                  className="flex-1 font-bold text-sm"
                >
                  {ex.blankNumber}.
                </label>
                {/* No FlagButton */}
              </div>

              {/* Disabled Text Input Field */}
              <input
                id={`input-${questionId}`}
                type="text"
                maxLength="3"
                value={correctAnswer || ''}
                disabled
                className="w-full px-3 py-2 border-2 border-gray-900 rounded-none text-sm bg-gray-100 cursor-not-allowed font-semibold"
              />
            </div>
          );
        })}

        {/* Questions List (Input Fields) */}
        {questions.map(q => {
          const questionId = q.questionId;
          const currentAnswer = partAnswers[questionId] || '';

          return (
            <div
              key={questionId}
              className="border border-t-0 border-gray-900 bg-white p-4 rounded-none hover:bg-gray-50 transition-colors"
            >
              {/* Question Label with Flag (Unchanged) */}
              <div className="flex items-start justify-between mb-2">
                <label
                  htmlFor={`input-${questionId}`}
                  className="flex-1 font-bold text-sm"
                >
                  {q.blankNumber}.
                </label>
                <FlagButton {...allProps} questionId={questionId} />
              </div>

              {/* Text Input Field (Unchanged) */}
              <input
                id={`input-${questionId}`}
                type="text"
                maxLength="3"
                value={currentAnswer}
                onChange={(e) => handleSingleAnswer(questionId, e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-900 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-1"
                placeholder={`Antwort für (${q.blankNumber})`}
              />
            </div>
          );
        })}
      </div>
    );

    const mobileOverlay = (isMobileAnswerSheetOpen && onCloseMobileAnswerSheet) ? (
      <div className="fixed inset-0 z-[60] flex flex-col justify-end md:hidden" aria-modal="true" role="dialog" aria-label="Antwortbogen">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCloseMobileAnswerSheet} aria-hidden />
        <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[90vh] flex flex-col animate-slide-up">
          <div className="flex items-center justify-between flex-shrink-0 p-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
            <h3 className="text-lg font-bold text-gray-900">Antwortbogen</h3>
            <button type="button" onClick={onCloseMobileAnswerSheet} className="p-2 -m-2 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation min-w-[48px] min-h-[48px] flex items-center justify-center" aria-label="Schließen">
              <X className="w-6 h-6 text-gray-700" />
            </button>
          </div>
          <div className="overflow-y-auto overscroll-contain p-4 pb-safe">{rightContent}</div>
        </div>
      </div>
    ) : null;

    return (
      <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-6">
        <div className="grid grid-cols-1 md:grid-cols-5 md:gap-8">

          {/* Left Column (Text) */}
          <div className="md:col-span-3 min-w-0">
            {leftContent}
          </div>

          {/* Right Column (Answer Sheet) - hidden on mobile */}
          <div className="hidden md:block md:col-span-2 mt-6 md:mt-0">
            <div className="md:sticky md:top-6">
              {rightContent}
            </div>
          </div>

        </div>
        {mobileOverlay}
      </div>
    );
});

export default TextReconstructionEndings;